import React, { useCallback, useEffect, useState } from "react";
import _ from "lodash";
import { StandardPageWrapper } from "../../../components/layout/StandardPageWrapper";
import { TopNavigationBreadcrumb } from "../../../components/privacyCenter/TopNavigationBreadcrumb";
import { useOrganizationVendors, usePrimaryOrganization } from "../../../hooks";
import { ToolTip, SelectMenuDropdown, Modal, ExplanatoryCard } from "@truevault/aquilo";
import {
  CollectionContext,
  DataAccessibility,
  DataDeletability,
  OrganizationDataRecipientDto,
} from "../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { PrivacyCenterIntegrationsDto } from "../../../../common/service/server/dto/PrivacyCenterIntegrationsDto";
import { useActionRequest } from "../../../../common/hooks/api";
import { useSurvey } from "../../../../common/hooks/api/useSurvey";
import { UpdateVendorArgs } from "../../data-map/components/workflows/create-data-recipient/ClassificationForDataRecipientPage";
import { Api } from "../../../../common/service/Api";
import { useSnackBar } from "@truevault/aquilo";
import { useRequestInstructions, useRetentionReasons } from "../../../hooks/useOrganizationVendors";
import { RequestHandlingInstructionDto } from "../../../../common/service/server/dto/RequestHandlingInstructionDto";
import { OrganizationPublicId, ProcessingMethod } from "../../../../common/service/server/Types";
import { useDataMapDisclosedPIC } from "../../../../common/service/resources/data-map/dataMapTransformers";
import { DataMapDto } from "../../../../common/service/server/dto/datamap/DataMapDto";
import { useDataMap } from "../../../../common/hooks/api/useDataMap";
import { RetentionReasonDto } from "../../../../common/service/server/dto/RetentionReasonDto";
import {
  isDataAccessibilityAndDeletionSettingsCompletedForVendor,
  resolveDataAccessibilityForVendor,
} from "../../../util/vendors";
import { useIntegrationsSettings } from "../../../components/superorg/settings/IntegrationsSettings";
import { BoltIcon, BoltSlashIcon, InformationCircleIcon } from "@heroicons/react/20/solid";
import { LightbulbIcon } from "@truevault/aquilo/src/components/selectMenu/SelectMenu";
import { DEVELOPER_INSTRUCTIONS_SURVEY_NAME } from "../PrivacyNoticeChecklistItems";
import { DevInstructionsFinishedDataStorageSettingsSetupSlug } from "../WorkflowsDashboardPage";
import { ProgressFooter, ProgressFooterActions } from "../../get-compliant/ProgressFooter";
import { useHistory } from "react-router";
import { Routes } from "../../../root/routes";
import { useLocalStorage } from "../../../hooks/useLocalStorage";

export function DataStorageSettingsPage() {
  const history = useHistory();

  const [org, ,] = usePrimaryOrganization();

  const [isComplete, setIsComplete] = useState(true);
  const [missingVendorNames, setMissingVendorNames] = useState([]);

  const { answers: devInstructionsSurveyAnswers, setAnswer: setDevSurveyAnswer } = useSurvey(
    org?.id,
    DEVELOPER_INSTRUCTIONS_SURVEY_NAME,
  );

  const didFinishDataStorageSettingsSetup =
    devInstructionsSurveyAnswers &&
    devInstructionsSurveyAnswers[DevInstructionsFinishedDataStorageSettingsSetupSlug] === "true";

  const [seenStorageSettingsSetupModal, setSeenStorageSettingsSetupModal] = useLocalStorage(
    "seen_data_storage_settings_setup_modal",
    "false",
  );

  return (
    <StandardPageWrapper
      fullWidth
      footer={
        (!didFinishDataStorageSettingsSetup || !isComplete) && (
          <ProgressFooter
            actions={
              <ProgressFooterActions
                nextContent="Done with Data Storage"
                nextOnClick={async () => {
                  await setDevSurveyAnswer(
                    DevInstructionsFinishedDataStorageSettingsSetupSlug,
                    "true",
                  );
                  history.push(Routes.workflows.dashboard);
                }}
                nextDisabled={!isComplete}
                nextDisabledTooltip={`Missing answers for: ${missingVendorNames.join(", ")}`}
              />
            }
          />
        )
      }
    >
      <TopNavigationBreadcrumb source="DSAR Workflows" title="Data Storage" />

      {!didFinishDataStorageSettingsSetup && seenStorageSettingsSetupModal !== "true" && (
        <Modal
          title="Review your data storage settings for your vendors and make changes if needed. Once done go to Request Workflows."
          firstButton="Let's start"
          firstButtonColor="primary"
          widthButton="full"
          closeButton={false}
          showOverlay={true}
          closeOnOverlayClick={false}
          onFirstButtonClick={() => setSeenStorageSettingsSetupModal("true")}
        >
          <img
            src={`/assets/images/stay_compliant/data_storage_settings_setup_modal.svg`}
            className="w-[267px] h-[239px] mx-auto"
          />
        </Modal>
      )}

      <div className="tw-boundary">
        <div>
          <ExplanatoryCard
            topBody={
              <div>
                <h3 className="text-lg font-medium leading-6 text-neutral-800 mb-3">
                  Data Deletion and Storage Settings
                </h3>
                <div className="text-sm font-normal leading-5 text-gray-500">
                  <p className="mb-3">
                    The table below contains a list of your vendors and systems where you store or
                    send personal information about your customers. Your business will be required
                    to delete personal information when customers make a Request to Delete.
                  </p>
                  <p>
                    The "Is data accessible" column asks whether you can look up a customer in that
                    system using a customer's name or email address. If you cannot do that, answer
                    "no."
                  </p>
                  <p>
                    The "Will data be deleted" column asks if you can delete that data or if you
                    have a business or legal basis for retaining the data.
                  </p>
                </div>
              </div>
            }
            hiddenBody={
              <div className="text-sm font-normal leading-5 text-gray-500 space-y-4">
                <p>
                  If you retain data, we'll ask you to select one or more reasons for retention.
                  That information will be provided to customers who request data deletion. Privacy
                  laws allow for the retention of data for several reasons, including:
                </p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>To fulfill a transaction a contract</li>
                  <li>For Internal Operations Uses (excludes marketing, sales uses)</li>
                  <li>To comply with a legal obligation</li>
                  <li>To detect or prevent security incidents</li>
                  <li>To debut or repair errors</li>
                </ul>
                <p>
                  Note: TrueVault defaults to "Yes" for data accessibility and data deletion for
                  most vendors.{" "}
                </p>
              </div>
            }
          />
          <div className="flex items-center gap-1 mt-1.5">
            <div className="text-primary-600">{LightbulbIcon}</div>
            <span className="text-primary-700 text-[16px] font-normal">Recommended selection.</span>
          </div>
        </div>

        <DataStorageVendorSettingsTable
          orgId={org?.id}
          vendorsCollectionContexts={["CONSUMER"]}
          setIsComplete={(newIsComplete: boolean, newMissingVendorNames: string[]) => {
            setIsComplete(newIsComplete);
            setMissingVendorNames(newMissingVendorNames);
          }}
        />
      </div>
    </StandardPageWrapper>
  );
}

export function DataStorageVendorSettingsTable({
  orgId,
  vendorsCollectionContexts,
  setIsComplete,
}: {
  orgId: OrganizationPublicId;
  vendorsCollectionContexts: CollectionContext[];
  setIsComplete?: (isComplete: boolean, missingVendorNames: string[]) => void;
}) {
  const enqueueSnackbar = useSnackBar();

  const { data: dataMap } = useDataMap(orgId);

  const [allPossibleRetentionReasons, ,] = useRetentionReasons();

  const [dataRecipients, , refreshDataRecipients] = useOrganizationVendors(
    orgId,
    /* excludeRecipientCategories = */ [],
    /* includeDataStorageLocations = */ false,
    /* excludeIncomplete = */ undefined,
    /* context = */ vendorsCollectionContexts,
    /* onlyUseSettled = */ true,
  );
  const { fetch: updateDataRecipient } = useActionRequest({
    api: async (v: UpdateVendorArgs) => {
      const { vendorId, vendorSettings } = v;
      await Api.organizationVendor.addOrUpdateVendor(orgId, vendorId, vendorSettings);
    },
    onSuccess: refreshDataRecipients,
    onError: () => {
      enqueueSnackbar(`Error updating vendor data accessibility settings`, { variant: "error" });
    },
  });
  const [fetchedDeletionInstructions, , refreshInstructions] = useRequestInstructions(
    orgId,
    "DELETE",
    /* onSuccess = */ undefined,
    /* onlyUseSettled = */ true,
  );
  const { fetch: updateInstruction } = useActionRequest({
    api: async (newInstruction: RequestHandlingInstructionDto) => {
      await Api.requestHandlingInstructions.putInstruction(orgId, newInstruction);
    },
    onSuccess: refreshInstructions,
    onError: () => {
      enqueueSnackbar(`Error updating vendor deletion settings`, { variant: "error" });
    },
  });

  const { settings: apiIntegrations } = useIntegrationsSettings();

  // Keep track of the completion of each row
  // Note that this is a duplication of state from the data recipient objects, but is only used to display real-time UI feedback
  const [dataRecipientCompletionById, setDataRecipientCompletionById] =
    useState<Map<string, boolean>>(null);
  // Keep track of when the data is initially loaded, whether we start out in a complete or incomplete state, so we can show the footer at the bottom if the data is initially incomplete
  // const [isInitiallyComplete, setIsInitiallyComplete] = useState(false);

  // Initialize `dataRecipientCompletionById` once the data recipients and deletion instructions are loaded
  useEffect(() => {
    if (
      !dataRecipients ||
      dataRecipients.length == 0 ||
      !fetchedDeletionInstructions ||
      fetchedDeletionInstructions.length == 0 ||
      !allPossibleRetentionReasons ||
      allPossibleRetentionReasons.length == 0 ||
      !dataMap ||
      dataRecipientCompletionById === null
    ) {
      return;
    }

    if (dataRecipientCompletionById === null) {
      let initMap = new Map<string, boolean>(
        dataRecipients.map((dr) => {
          let deleteInstruction = fetchedDeletionInstructions.find(
            (di) => di.vendorId == dr.vendorId,
          );
          return [
            dr.id,
            deleteInstruction &&
              isDataAccessibilityAndDeletionSettingsCompletedForVendor({
                dataAccessibility: dr.dataAccessibility,
                dataDeletability: dr.dataDeletability,
                deletionProcessingMethod: deleteInstruction.processingMethod,
                retentionReasons: deleteInstruction.retentionReasons,
                hasRetentionExceptions: deleteInstruction.hasRetentionExceptions,
                deletedPIC: deleteInstruction.deletedPIC,
              }),
          ];
        }),
      );
      setDataRecipientCompletionById(initMap);

      // setIsInitiallyComplete([...initMap.values()].every((v) => v === true));
      let isComplete = [...dataRecipientCompletionById.values()].every((v) => v === true);
      let missingVendorNames = Array.from(dataRecipientCompletionById.keys())
        .filter((k) => dataRecipientCompletionById.get(k) === false)
        .map((k) => dataRecipients.find((dr) => dr.id == k)?.name ?? null)
        .filter((v) => v !== null)
        .sort();
      setIsComplete(isComplete, missingVendorNames);
    }
  }, [dataRecipients, fetchedDeletionInstructions, dataRecipientCompletionById]);

  if (
    !dataRecipients ||
    dataRecipients.length == 0 ||
    !fetchedDeletionInstructions ||
    fetchedDeletionInstructions.length == 0
  ) {
    return null;
  }

  return (
    <div className="border border-solid border-gray-200 rounded-lg max-w-[1160px] mt-12 max-h-[636px] overflow-y-auto">
      <table>
        <colgroup>
          <col style={{ width: "220px" }} />
          <col style={{ width: "220px" }} />
          <col style={{ width: "220px" }} />
          <col style={{ width: "250px" }} />
          <col style={{ width: "250px" }} />
        </colgroup>
        <thead className="sticky top-0 z-10">
          <tr>
            <th
              scope="col"
              className="bg-[#F9FAFB] h-[40px] border-b border-solid border-gray-200 rounded-tl-lg pl-6 text-left text-xs font-medium text-gray-500 uppercase"
            >
              Vendor Name
            </th>
            <th scope="col" className="bg-[#F9FAFB] h-[40px]">
              <div className="flex items-center justify-center gap-3">
                <span className="text-xs font-medium text-gray-500 uppercase">
                  Is Data Accessible
                </span>
                <ToolTip
                  text="Answer 'yes' if you can view and copy/retrieve stored personal information."
                  width="210px"
                >
                  <InformationCircleIcon className="w-[16px] h-[16px] text-blue-gray-900" />
                </ToolTip>
              </div>
            </th>
            <th scope="col" className="bg-[#F9FAFB] h-[40px]">
              <div className="flex items-center justify-center gap-3">
                <span className="text-xs font-medium text-gray-500 uppercase">
                  Will Data Be Deleted
                </span>
                <ToolTip
                  text="Answer 'yes' unless you need to keep data for legal or business reasons, or can't technically delete the data."
                  width="210px"
                >
                  <InformationCircleIcon className="w-[16px] h-[16px] text-blue-gray-900" />
                </ToolTip>
              </div>
            </th>
            <th scope="col" className="bg-[#F9FAFB] h-[40px]">
              <div className="flex items-center justify-center gap-3">
                <span className="text-xs font-medium text-gray-500 uppercase">
                  Reasons For Retention
                </span>
                <ToolTip
                  text={`Privacy laws allow you to retain personal data for certain approved business purposes, known as "exceptions" to deletion.`}
                  width="230px"
                >
                  <InformationCircleIcon className="w-[16px] h-[16px] text-blue-gray-900" />
                </ToolTip>
              </div>
            </th>
            <th scope="col" className="bg-[#F9FAFB] h-[40px] rounded-tr-lg">
              <div className="flex items-center justify-center gap-3">
                <span className="text-xs font-medium text-gray-500 uppercase">
                  Partial Deletion (Optional)
                </span>
                <ToolTip
                  text={`Select the data you will delete if you chose 'partial' deletion for the deletion column.`}
                  width="230px"
                >
                  <InformationCircleIcon className="w-[16px] h-[16px] text-blue-gray-900" />
                </ToolTip>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {dataRecipients.map((dataRecipient) => {
            const deletionInstruction = fetchedDeletionInstructions.find(
              (ins) => ins.vendorId == dataRecipient.vendorId,
            );
            return (
              <DataRecipientRow
                key={dataRecipient.id}
                dataRecipient={dataRecipient}
                updateDataRecipient={updateDataRecipient}
                deletionInstruction={deletionInstruction}
                updateInstruction={updateInstruction}
                dataMap={dataMap}
                allPossibleRetentionReasons={allPossibleRetentionReasons}
                apiIntegrations={apiIntegrations}
                setCompletionForDataRecipient={(drId: string, completion: boolean) => {
                  let newCompletionMapFn = (prev: Map<string, boolean>) => {
                    let newMap = new Map(prev);
                    newMap.set(drId, completion);
                    return newMap;
                  };

                  setDataRecipientCompletionById(newCompletionMapFn);

                  let isComplete = [
                    ...newCompletionMapFn(dataRecipientCompletionById).values(),
                  ].every((v) => v === true);
                  let missingVendorNames = Array.from(dataRecipientCompletionById.keys())
                    .filter((k) => dataRecipientCompletionById.get(k) === false)
                    .map((k) => dataRecipients.find((dr) => dr.id == k)?.name ?? null)
                    .filter((v) => v !== null)
                    .sort();
                  setIsComplete(isComplete, missingVendorNames);
                }}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const INTERNAL_OPERATIONS_USES = "internal-operations-uses";

function DataRecipientRow({
  dataRecipient,
  deletionInstruction,
  updateDataRecipient,
  updateInstruction,
  setCompletionForDataRecipient,
  dataMap,
  allPossibleRetentionReasons,
  apiIntegrations,
}: {
  dataRecipient: OrganizationDataRecipientDto;
  deletionInstruction: RequestHandlingInstructionDto;
  updateDataRecipient: (v: UpdateVendorArgs) => Promise<void>;
  updateInstruction: (v: RequestHandlingInstructionDto) => Promise<void>;
  setCompletionForDataRecipient: (id: string, completed: boolean) => void;
  dataMap: DataMapDto;
  allPossibleRetentionReasons: RetentionReasonDto[];
  apiIntegrations: PrivacyCenterIntegrationsDto;
}) {
  const [dataAccessibility, setDataAccessibility] = useState<DataAccessibility | null>(
    dataRecipient.dataAccessibility,
  );
  const [dataDeletability, setDataDeletability] = useState<DataDeletability | null>(
    dataRecipient.dataDeletability,
  );
  const [deleteProcessingMethod, setDeleteProcessingMethod] = useState<ProcessingMethod>(
    deletionInstruction?.processingMethod,
  );
  const [selectedRetentionReasons, setSelectedRetentionReasons] = useState<string[]>(
    deletionInstruction?.retentionReasons ?? [],
  );
  const [hasRetentionExceptions, setHasRetentionExceptions] = useState<boolean>(
    deletionInstruction?.hasRetentionExceptions,
  );
  const [selectedDeletedPics, setSelectedDeletedPics] = useState<string[]>(
    deletionInstruction?.deletedPIC ?? [],
  );

  const [
    showReasonableInternalUseConfirmationModal,
    setShowReasonableInternalUseConfirmationModal,
  ] = useState(false);

  const disclosedCategories = useDataMapDisclosedPIC(dataMap, {
    vendorId: dataRecipient.vendorId ?? dataRecipient.id,
  });

  const debouncedSaveRetentionReasons = useCallback(
    _.debounce(async (newRetentionReasons: string[]) => {
      // console.log(`sending update instruction with new retention reasons:`, newRetentionReasons);
      await updateInstruction({
        ...deletionInstruction,
        retentionReasons: newRetentionReasons,
      });
    }, 1000),
    [deletionInstruction],
  );

  const debouncedSaveDeletedPics = useCallback(
    _.debounce(async (newDeletedPics: string[]) => {
      await updateInstruction({
        ...deletionInstruction,
        deletedPIC: newDeletedPics,
      });
    }, 1000),
    [deletionInstruction],
  );

  // ==============
  // Computed Values
  // ==============

  let lockedValues = resolveDataAccessibilityForVendor(
    {
      ...dataRecipient,
      dataAccessibility: dataAccessibility,
      dataDeletability: dataDeletability,
    },
    apiIntegrations,
  );

  // const isDeleteProcessingMethodLocked = dataAccessibility == "NOT_ACCESSIBLE";

  let finalDataAccessibility = lockedValues.effectiveDataAccessibility;
  let allowEditingDataAccessibility = lockedValues.dataAccessibilityIsLocked !== true;

  let finalDeleteProcessingMethod = lockedValues.deletionProcessingMethod
    ? lockedValues.deletionProcessingMethod
    : deleteProcessingMethod;
  let allowEditingDeleteProcessingMethod = lockedValues.deletionProcessingMethodIsLocked !== true;
  // We want to show the delete processing method even if dataAccessibility is null since we (implicitly) default to ACCESSIBLE
  let showDeleteProcessingMethod =
    (finalDataAccessibility == "ACCESSIBLE" || finalDataAccessibility == null) &&
    deletionInstruction;

  let finalDataDeletability = lockedValues.dataDeletability
    ? lockedValues.dataDeletability
    : dataDeletability;
  // let allowEditingDataDeletability = lockedValues.dataDeletabilityIsLocked !== true;

  let allowEditingReasonsForRetention =
    finalDataAccessibility != null &&
    showDeleteProcessingMethod &&
    finalDataDeletability != "NOT_DELETABLE" &&
    finalDeleteProcessingMethod == "RETAIN";

  let allowEditingPartialDeletion =
    showDeleteProcessingMethod &&
    finalDataDeletability != "NOT_DELETABLE" &&
    finalDeleteProcessingMethod == "RETAIN" &&
    hasRetentionExceptions;

  let vendorApiConnection = (apiIntegrations?.vendors || []).find(
    (apiIntegration) => apiIntegration.vendorId == dataRecipient.vendorId,
  );

  return (
    <>
      <tr className="border-t border-solid border-gray-200">
        <td className="px-6 py-3.5">
          <div className="flex items-center gap-3">
            <img src={dataRecipient.logoUrl} alt={dataRecipient.name} className="h-6 w-6" />
            <span className="text-[#111827] font-medium text-sm">{dataRecipient.name}</span>
            {vendorApiConnection &&
              (vendorApiConnection.connected ? (
                <BoltIcon className="h-4 w-4 text-green-600 bg-[#E2FDF3] p-0.5 rounded-3xl" />
              ) : (
                <BoltSlashIcon className="h-4 w-4 text-neutral-700 bg-[#F5F4F9] p-0.5 rounded-3xl" />
              ))}
          </div>
        </td>
        <td className="text-center">
          <ToolTip text={lockedValues.dataAccessibilityLockedTooltipMessage} width="250px">
            <SelectMenuDropdown<DataAccessibility>
              items={[
                {
                  label: "Yes",
                  value: "ACCESSIBLE",
                  isSelected:
                    finalDataAccessibility == "ACCESSIBLE" || finalDataAccessibility == null,
                },
                {
                  label: "No",
                  value: "NOT_ACCESSIBLE",
                  isSelected: finalDataAccessibility == "NOT_ACCESSIBLE",
                },
              ]}
              isLocked={!allowEditingDataAccessibility}
              lockedItemLeftMargin="ml-[-24px]"
              onSelect={async (newSelectedDataAccessibility: DataAccessibility) => {
                setDataAccessibility(newSelectedDataAccessibility);

                let updatedDataRecipient = {
                  vendorId: dataRecipient.vendorId,
                  vendorSettings: {
                    ...dataRecipient,
                    dataAccessibility: newSelectedDataAccessibility,
                    classificationId: dataRecipient.classification.id,
                  },
                };

                updateDataRecipient(updatedDataRecipient);

                setCompletionForDataRecipient(
                  dataRecipient.id,
                  isDataAccessibilityAndDeletionSettingsCompletedForVendor({
                    dataAccessibility: newSelectedDataAccessibility,
                    dataDeletability: finalDataDeletability,
                    deletionProcessingMethod: finalDeleteProcessingMethod,
                    retentionReasons: selectedRetentionReasons,
                    hasRetentionExceptions,
                    deletedPIC: selectedDeletedPics,
                  }),
                );
              }}
              dropdownWidthPx={224}
              rightPositionPx={-176}
            />
          </ToolTip>
        </td>
        <td className="text-center">
          {showDeleteProcessingMethod ? (
            // <ToolTip
            //   text={lockedValues.dataDeletabilityLockedTooltipMessage}
            //   width="250px"
            // >
            /* We need RETAIN_PARTIAL because when the user selects "Partial", we still store it in the database as "RETAIN", but also need to set hasRetentionExceptions to true */
            <SelectMenuDropdown<ProcessingMethod | "PARTIAL_DELETED" | "NOT_DELETABLE">
              items={[
                {
                  label: "Yes - data can be deleted",
                  previewLabel: "Yes",
                  value: "DELETE",
                  isSelected:
                    finalDeleteProcessingMethod == "DELETE" &&
                    finalDataDeletability !== "NOT_DELETABLE" &&
                    // The reason to add a check for null data accessibility to each `isSelected:` is because if the data accessibility is null, then we want to not show any selected answer, to force the user to select a value (which will update both the data accessibility and the deletion processing method)
                    finalDataAccessibility != null,
                },
                {
                  label: "No - data must be retained",
                  previewLabel: "No - retained",
                  value: "RETAIN",
                  isSelected:
                    finalDeleteProcessingMethod == "RETAIN" &&
                    finalDataDeletability !== "NOT_DELETABLE" &&
                    !hasRetentionExceptions &&
                    finalDataAccessibility != null,
                  isRecommended: dataRecipient.defaultRetentionReasons?.length > 0,
                },
                {
                  label: "Partial - some data deleted",
                  previewLabel: "Partial",
                  value: "PARTIAL_DELETED",
                  isSelected:
                    finalDeleteProcessingMethod == "RETAIN" &&
                    finalDataDeletability !== "NOT_DELETABLE" &&
                    hasRetentionExceptions &&
                    finalDataAccessibility != null,
                },
                {
                  label: "No - not able to delete data",
                  previewLabel: "No - unable",
                  value: "NOT_DELETABLE",
                  isSelected:
                    finalDataDeletability == "NOT_DELETABLE" && finalDataAccessibility != null,
                },
              ]}
              onSelect={async (
                selectedValue: ProcessingMethod | "PARTIAL_DELETED" | "NOT_DELETABLE",
              ) => {
                let newHasRetentionExceptions = selectedValue == "PARTIAL_DELETED";
                let newDataDeletability: DataDeletability =
                  selectedValue == "NOT_DELETABLE" ? "NOT_DELETABLE" : "DELETABLE";
                let newDeleteInstructionProcessingMethod: ProcessingMethod =
                  selectedValue == "DELETE" ? "DELETE" : "RETAIN";

                // These fields are only sometimes updated, so default to the existing values
                let newSelectedRetentionReasons = selectedRetentionReasons;
                let newDataAccessibility = finalDataAccessibility;

                // If data deletability is changing from "Yes" to "Partial" or "No", and there are no existing selected retention reasons, then set the default retention reasons for that vendor (if they exist)
                if (
                  finalDataDeletability == "DELETABLE" &&
                  newDataDeletability != "DELETABLE" &&
                  selectedRetentionReasons.length == 0 &&
                  dataRecipient.defaultRetentionReasons?.length > 0
                ) {
                  newSelectedRetentionReasons = dataRecipient.defaultRetentionReasons;
                }

                // If data deletability is "No", then always set the delete processing method to "RETAIN"
                if (newDataDeletability == "NOT_DELETABLE") {
                  newDeleteInstructionProcessingMethod = "RETAIN";
                }

                // If data accessibility is previously null, then apply the default value of ACCESSIBLE
                if (finalDataAccessibility == null) {
                  newDataAccessibility = "ACCESSIBLE";
                }

                setHasRetentionExceptions(newHasRetentionExceptions);
                setDataDeletability(newDataDeletability);
                setDeleteProcessingMethod(newDeleteInstructionProcessingMethod);
                setSelectedRetentionReasons(newSelectedRetentionReasons);
                setDataAccessibility(newDataAccessibility);

                let updatedInstruction = {
                  ...deletionInstruction,
                  processingMethod: newDeleteInstructionProcessingMethod,
                  hasRetentionExceptions: newHasRetentionExceptions,
                  retentionReasons: newSelectedRetentionReasons,
                };

                let updatedDataRecipient = {
                  vendorId: dataRecipient.vendorId,
                  vendorSettings: {
                    ...dataRecipient,
                    dataAccessibility: newDataAccessibility,
                    dataDeletability: newDataDeletability,
                    classificationId: dataRecipient.classification.id,
                  },
                };

                await Promise.all([
                  updateDataRecipient(updatedDataRecipient),
                  updateInstruction(updatedInstruction),
                ]);

                setCompletionForDataRecipient(
                  dataRecipient.id,
                  isDataAccessibilityAndDeletionSettingsCompletedForVendor({
                    dataAccessibility: newDataAccessibility,
                    dataDeletability: newDataDeletability,
                    deletionProcessingMethod: newDeleteInstructionProcessingMethod,
                    retentionReasons: newSelectedRetentionReasons,
                    hasRetentionExceptions: newHasRetentionExceptions,
                    deletedPIC: updatedInstruction.deletedPIC,
                  }),
                );
              }}
              noSelectedErrorMessage="Choose answer"
              isLocked={!allowEditingDeleteProcessingMethod}
              lockedItemLeftMargin="ml-[-24px]"
              dropdownWidthPx={300}
              rightPositionPx={-120}
            />
          ) : (
            // </ToolTip>
            <hr className="w-[51px] text-neutral-700 mx-auto" />
          )}
        </td>
        <td className="text-center py-2.5">
          {allowEditingReasonsForRetention ? (
            <SelectMenuDropdown<string>
              items={allPossibleRetentionReasons.map((reason) => ({
                label: reason.reason,
                value: reason.slug,
                isSelected: selectedRetentionReasons.includes(reason.slug),
                isRecommended: dataRecipient.defaultRetentionReasons.includes(reason.slug),
              }))}
              allowMultipleSelected={true}
              noSelectedErrorMessage="Choose reason(s)"
              onSelect={async (selectedValue: string) => {
                // If user is trying to select the "Reasonable Internal Use" exception, show a confirmation modal (only when selecting, not when un-selecting)
                if (
                  selectedValue === INTERNAL_OPERATIONS_USES &&
                  !selectedRetentionReasons.includes(INTERNAL_OPERATIONS_USES)
                ) {
                  setShowReasonableInternalUseConfirmationModal(true);
                  return;
                }

                const newSelectedRetentionReasons = selectedRetentionReasons.includes(selectedValue)
                  ? selectedRetentionReasons.filter((r) => r != selectedValue)
                  : [...selectedRetentionReasons, selectedValue];

                setSelectedRetentionReasons(newSelectedRetentionReasons);
                debouncedSaveRetentionReasons(newSelectedRetentionReasons);

                setCompletionForDataRecipient(
                  dataRecipient.id,
                  isDataAccessibilityAndDeletionSettingsCompletedForVendor({
                    dataAccessibility: finalDataAccessibility,
                    dataDeletability: finalDataDeletability,
                    deletionProcessingMethod: finalDeleteProcessingMethod,
                    retentionReasons: newSelectedRetentionReasons,
                    hasRetentionExceptions,
                    deletedPIC: selectedDeletedPics,
                  }),
                );
              }}
              dropdownWidthPx={350}
              rightPositionPx={-194}
            />
          ) : (
            <hr className="w-[51px] text-neutral-700 mx-auto" />
          )}
        </td>
        <td className="text-center py-2.5">
          {allowEditingPartialDeletion ? (
            <SelectMenuDropdown<string>
              items={disclosedCategories.map((cat) => ({
                label: cat.name,
                value: cat.id,
                isSelected: selectedDeletedPics.includes(cat.id),
              }))}
              allowMultipleSelected={true}
              noSelectedErrorMessage="What will be deleted"
              selectedOverflowItemName="categories"
              onSelect={async (selectedValue: string) => {
                const newSelectedDeletedPics = selectedDeletedPics.includes(selectedValue)
                  ? selectedDeletedPics.filter((r) => r != selectedValue)
                  : [...selectedDeletedPics, selectedValue];

                setSelectedDeletedPics(newSelectedDeletedPics);
                debouncedSaveDeletedPics(newSelectedDeletedPics);

                setCompletionForDataRecipient(
                  dataRecipient.id,
                  isDataAccessibilityAndDeletionSettingsCompletedForVendor({
                    dataAccessibility: finalDataAccessibility,
                    dataDeletability: finalDataDeletability,
                    deletionProcessingMethod: finalDeleteProcessingMethod,
                    retentionReasons: selectedRetentionReasons,
                    hasRetentionExceptions,
                    deletedPIC: newSelectedDeletedPics,
                  }),
                );
              }}
              dropdownWidthPx={232}
              rightPositionPx={-65}
            />
          ) : (
            <hr className="w-[51px] text-neutral-700 mx-auto" />
          )}
        </td>
      </tr>
      {showReasonableInternalUseConfirmationModal && (
        <ReasonableInternalUseConfirmationModal
          closeModal={() => setShowReasonableInternalUseConfirmationModal(false)}
          onConfirm={async () => {
            const newRetentionReasons = selectedRetentionReasons.includes(INTERNAL_OPERATIONS_USES)
              ? selectedRetentionReasons
              : [...selectedRetentionReasons, INTERNAL_OPERATIONS_USES];

            setSelectedRetentionReasons(newRetentionReasons);
            debouncedSaveRetentionReasons(newRetentionReasons);

            setShowReasonableInternalUseConfirmationModal(false);

            setCompletionForDataRecipient(
              dataRecipient.id,
              isDataAccessibilityAndDeletionSettingsCompletedForVendor({
                dataAccessibility: finalDataAccessibility,
                dataDeletability: finalDataDeletability,
                deletionProcessingMethod: finalDeleteProcessingMethod,
                retentionReasons: newRetentionReasons,
                hasRetentionExceptions,
                deletedPIC: selectedDeletedPics,
              }),
            );
          }}
        />
      )}
    </>
  );
}

function ReasonableInternalUseConfirmationModal({
  closeModal,
  onConfirm,
}: {
  closeModal: () => void;
  onConfirm: () => Promise<void>;
}) {
  return (
    <Modal
      title="Is your internal use consistent with consumer expectations?"
      alignText="left"
      firstButton="Cancel"
      firstButtonColor="tertiary"
      onFirstButtonClick={closeModal}
      alignButton="right"
      secondButton="Yes, It Is"
      secondButtonColor="primary"
      onSecondButtonClick={onConfirm}
      showOverlay
      closeOnOverlayClick={false}
      onClose={closeModal}
    >
      <p className="text-gray-500 text-[14px] font-normal mt-2">
        Data retained under the “Internal Operations Uses” exception may be used only in a manner
        consistent with what a consumer would expect in the context in which your business collected
        the data. Confirm that your business's use of the data conforms to this requirement to
        select this exception.
      </p>
    </Modal>
  );
}
